import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, delay, scan, shareReplay, tap } from 'rxjs/operators';
import { ReviewComponent } from 'src/app/component/review/review.component';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { LoadingService } from 'src/app/services/loading.service';

import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { InAppBrowser, InAppBrowserOptions } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { BusticketService } from '@core/services';
import { ScannedResponse } from '@core/services/scanner/scanner.service';
import {
  ActionSheetController,
  AlertController,
  IonContent,
  ModalController,
  RefresherCustomEvent,
} from '@ionic/angular';
import { ScanningModalComponent } from '@shared/components/scanning-modal/scanning-modal.component';
import { ApiResponse, InfiniteScrollCustomEvent } from '@shared/models';
import { BusesGps, Ticket } from '@shared/models/tickets.model';

const IAB_OPTIONS: InAppBrowserOptions = {
  location: 'no',
  hidenavigationbuttons: 'yes',
  toolbarposition: 'bottom',
  toolbarcolor: '#0e53a1',
  navigationbuttoncolor: '#ffffff',
  hideurlbar: 'yes',
  closebuttoncaption: 'Cerrar',
};
@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.page.html',
  styleUrls: ['./tickets.page.scss'],
})
export class TicketsPage {
  @ViewChild(IonContent) content!: IonContent;
  infiniteScroll: InfiniteScrollCustomEvent | null = null;
  private loadTicketsNextSubject: Subject<number> = new Subject();
  nextTicketListCurrentPage$: Observable<number> = this.loadTicketsNextSubject.asObservable();
  private loadTicketsPreviousSubject: Subject<number> = new Subject();
  previousTicketListCurrentPage$: Observable<number> = this.loadTicketsPreviousSubject.asObservable();
  ticketsNext$: Observable<ApiResponse<Ticket[]>> = this.nextTicketListCurrentPage$.pipe(
    delay(0),
    tap(() => (this.loadingTickets = true)),
    concatMap((page) => this.getTicketsNext(page)),
    scan((acc, response) => this.accumulateTickets(acc, response)),
    tap(() => {
      this.loadingTickets = false;
      this.infiniteScroll?.target.complete();
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  ticketsPrevious$: Observable<ApiResponse<Ticket[]>> = this.previousTicketListCurrentPage$.pipe(
    delay(0),
    tap(() => (this.loadingTickets = true)),
    concatMap((page) => this.getBusticketsPrevious(page)),
    scan((acc, response) => this.accumulateTickets(acc, response)),
    tap(() => {
      this.loadingTickets = false;
      this.infiniteScroll?.target.complete();
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  loadingTickets = false;
  show = 'next';

  constructor(
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private router: Router,
    private dataService: DataService,
    private iab: InAppBrowser,
    private actionSheetController: ActionSheetController,
    private loadingService: LoadingService,
    private alertCtrl: AlertController,
    private busticketService: BusticketService
  ) {}

  ionViewDidEnter() {
    this.refresh();
  }

  doRefresh(refresh: RefresherCustomEvent) {
    if (this.show === 'next') {
      this.loadTicketsNextSubject.next(1);
    } else {
      this.loadTicketsPreviousSubject.next(1);
    }
    setTimeout(() => {
      refresh.target.complete();
    }, 1000);
  }

  doInfinite(infiniteScroll: InfiniteScrollCustomEvent, tickets: ApiResponse<Ticket[]>) {
    if (tickets.current_page === tickets.last_page) {
      infiniteScroll.target.complete();
      return;
    }
    this.infiniteScroll = infiniteScroll;
    if (this.show === 'next') {
      this.loadTicketsNextSubject.next(tickets.current_page ? tickets.current_page + 1 : 1);
    } else {
      this.loadTicketsPreviousSubject.next(tickets.current_page ? tickets.current_page + 1 : 1);
    }
  }

  goTicket(ticket: Ticket) {
    this.dataService.setData(ticket.id, ticket);
    this.router.navigate(['/tecservice-transportec/tickets/ticket/', ticket.id]);
  }

  async review(id: number) {
    const modal = await this.modalCtrl.create({
      component: ReviewComponent,
      componentProps: {
        id: null,
        busticketId: id,
      },
      cssClass: 'modal-fullscreen',
      keyboardClose: true,
      showBackdrop: true,
    });
    modal.onDidDismiss().then(() => {
      this.refresh();
    });

    return await modal.present();
  }

  goHelpList(id: number) {
    this.router.navigate(['/tecservice-transportec/tickets/ticket/' + id + '/help']);
  }

  onTrack(ticket: Ticket, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!ticket.buses_gps?.length) {
      return;
    }

    if (ticket.buses_gps?.length === 1) {
      this.iab.create(ticket.buses_gps[0].url_gps, '_blank', IAB_OPTIONS);
      return;
    }

    if (ticket.buses_gps?.length > 1) {
      this.presentTrackOptionsAlert(ticket.buses_gps);
    }
  }

  async presentTrackOptionsAlert(options: BusesGps[]) {
    const buttons = options.map((option) => {
      return {
        text: option.name,
        handler: () => {
          this.iab.create(option.url_gps, '_blank', IAB_OPTIONS);
        },
      };
    });
    const actionSheet = await this.actionSheetController.create({
      header: 'Autobuses',
      buttons,
    });
    await actionSheet.present();
  }

  async buyTicketByQr(): Promise<void> {
    try {
      const modal = await this.modalCtrl.create({
        component: ScanningModalComponent,
      });
      await modal.present();
      const { data } = await modal.onWillDismiss<ScannedResponse>();
      if (!data) {
        return;
      }
      this.onScanned(data);
    } catch (error) {
      console.log('Error scanning', error);
    }
  }

  async onScanned(response: ScannedResponse): Promise<void> {
    if (!response) {
      return;
    }
    if (!response.code || !this.isSellCodeValid(response.code)) {
      this.wrongQr();
      return;
    }
    try {
      const codeArray = response.code.split('|');
      const realtripId = +codeArray[1];
      const stopId = +codeArray[2];
      const { latitude, longitude } = response.coords || {};
      const ticketInfo = await this.getTicketInfo({ realtripId, stopId, latitude, longitude });
      const paymentConfirmation = await this.presentConfirmationDialog(ticketInfo);
      (await paymentConfirmation) &&
        this.payTicket({ realtripId, stopId, latitude, longitude, price: ticketInfo.price });
    } catch (error) {
      this.wrongQr();
      return;
    }
  }

  isSellCodeValid(code: string) {
    const codeArray = code.split('|');
    // Check if it is a ticket sell code
    if (codeArray.length !== 5 || codeArray[0] !== 'ticketsell') {
      return false;
    }

    // Check if the realtrip.id and current stop.id are valid
    if (isNaN(+codeArray[1]) || isNaN(+codeArray[2])) {
      return false;
    }

    // Check if the qr code is from now (5 min window)
    /*
    const scanDate = new Date(codeArray[3]);
    const now = new Date();
    if (Math.abs(differenceInMinutes(now, scanDate)) > 5) {
      return false;
    }*/
    return true;
  }

  async wrongQr(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Código QR incorrecto',
      message: 'Favor de verificar código o escanear nuevamente.',
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async wrongTx(message?: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Oops!',
      message: 'Algo salió mal, favor de intentarlo nuevamente. ' + message,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async getTicketInfo(data: { realtripId: number; stopId: number; latitude?: number; longitude?: number }) {
    await this.loadingService.present('Recuperando información...');
    return await this.apiService
      .getData(`realtrips/scan?realtrip_id=${data.realtripId}&stop_id=${data.stopId}`)
      .then((response) => {
        this.loadingService.dismiss();
        if (!response || response.error) {
          this.wrongTx(response.error);
        }
        return response;
      })
      .catch(() => {
        this.wrongTx();
        this.loadingService.dismiss();
        return;
      });
  }

  async presentConfirmationDialog(data: {
    route_name: string;
    stop_name: string;
    stop2_name: string;
    price: number;
    date: string;
  }): Promise<boolean> {
    const { route_name, stop_name, stop2_name, price } = data;
    if (!route_name) {
      return false;
    }
    const alert = await this.alertCtrl.create({
      header: `Compra ${route_name}`,
      message: `¿Desea comprar boleto saliendo de ${stop_name} en dirección a ${stop2_name} con un costo de $${price}?`,
      buttons: [
        {
          text: 'No, cancelar',
          role: 'cancel',
        },
        {
          text: 'Sí, continuar',
          role: 'pay',
        },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role !== 'pay') {
      return false;
    }
    return true;
  }

  async payTicket(data: {
    realtripId: number;
    stopId: number;
    latitude?: number;
    longitude?: number;
    price: number;
  }): Promise<void> {
    await this.loadingService.present('Recuperando boleto...');
    this.apiService
      .postData('bustickets/scan', {
        realtrip_id: data.realtripId,
        stop_id: data.stopId,
        method: 'wallet',
        lat: data.latitude,
        long: data.longitude,
        amount: data.price,
      })
      .then((response) => {
        this.loadingService.dismiss();
        if (!response || response.error) {
          this.wrongTx(response.error);
          return;
        }
        if (response?.busticket) {
          this.goTicket(response.busticket);
        } else {
          this.wrongTx(response.message || '');
        }
      })
      .catch(() => {
        this.wrongTx();
        this.loadingService.dismiss();
      });
  }

  private refresh() {
    this.loadTicketsNextSubject.next(1);
    this.loadTicketsPreviousSubject.next(1);
  }

  private getTicketsNext(page: number): Observable<ApiResponse<Ticket[]>> {
    return this.busticketService.getBusticketsNext({ page }).pipe(catchError(() => of({ data: [] })));
  }

  private getBusticketsPrevious(page: number): Observable<ApiResponse<Ticket[]>> {
    return this.busticketService.getBusticketsPrevious({ page }).pipe(catchError(() => of({ data: [] })));
  }

  private accumulateTickets(acc: ApiResponse<Ticket[]>, response: ApiResponse<Ticket[]>): ApiResponse<Ticket[]> {
    if (!response) {
      return acc;
    }
    if (response.current_page === 1) {
      return response;
    }
    return {
      ...response,
      data: [...acc.data, ...response.data],
    };
  }
}
